import { el } from './dom';
import splashFilmWebm from '@/assets/splash/lush-glade-splash.webm';
import splashFilmMp4 from '@/assets/splash/lush-glade-splash.mp4';
import splashStillWebp from '@/assets/splash/lush-glade-splash-poster.webp';

/**
 * The title card the app opens on.
 *
 * ## Shape of the thing
 *
 * The film starts immediately and the whole opening lasts exactly as long as the film — 4.2
 * seconds, mount to gone. There is no waiting beat in front of it, because the film already opens
 * on one: its own first frame is empty and the mark takes a second and a third to fade up. The
 * scrim washes in underneath over four-tenths of a second, fast enough not to be a wait and slow
 * enough not to be a flash of black over the editor.
 *
 * ## The dissolve rides the film's own tail
 *
 * The film fades itself out over its last half-second. Rather than wait for that to finish and then
 * fade the scrim — which would cost another half-second and read as two endings — the scrim's
 * dissolve is scheduled to overlap it, starting at `showMs - dissolveMs`. The mark and the curtain
 * come off together in one crossfade, which is why {@link SplashPlan.totalMs} is the length of the
 * film and not the film plus a dissolve.
 *
 * That overlap is the reason the timings here are not free parameters: `dissolveMs` is 550ms
 * because the encoded fade-out is ~500ms, and the two are meant to land together.
 *
 * ## Three copies of one piece of artwork
 *
 * The film ships as alpha-channel VP9 and as H.264, and the same frame ships again as a still. That
 * is not belt-and-braces for its own sake — each one covers a case the others cannot:
 *
 * - **WebM/VP9 with alpha** is the real thing. The mark carries its own transparency, so it
 *   composites onto the scrim with no frame around it at all.
 * - **MP4/H.264** is for decoders that will not read an alpha WebM, Safari chiefly. This copy has
 *   the dark backdrop baked in, which is the reason the scrim behind it is the same near-black
 *   green the film was graded against, and the reason the edges get feathered in CSS: matched
 *   colour plus a soft edge is what keeps that copy from reading as a rectangle pasted on top.
 * - **The still** covers the two cases where no video plays at all — a browser that refuses
 *   autoplay, and a reader who has asked for reduced motion. Both get the same artwork, held for a
 *   shorter beat, which is a quieter opening rather than a broken one.
 *
 * ## It is always skippable
 *
 * Every path through here ends, and any click or keypress ends it early. Nothing behind the splash
 * waits on it: the session probe and the editor boot in parallel underneath, and {@link finished}
 * exists for callers who want to know when the layer is gone, not as a gate anything must pass.
 */

/**
 * Dead air before the film starts. Zero: the film opens on its own fade-up, so a hold in front of
 * it would be a second of nothing rather than a beat. Kept as a parameter rather than deleted
 * because it is the one knob worth having if the artwork is ever replaced with a film that opens
 * cold, and {@link SplashPlan} accounts for it throughout.
 */
export const SPLASH_HOLD_MS = 0;

/** Length of the film itself. Matches the encoded assets; changing one without the other desyncs. */
export const SPLASH_FILM_MS = 4200;

/** How long the still is held when there is no film to play. Shorter: a still has nothing to say. */
export const SPLASH_STILL_MS = 1900;

/** The dissolve back to the editor. */
export const SPLASH_DISSOLVE_MS = 550;

export type SplashPhase = 'held' | 'showing' | 'dissolving' | 'done';

export interface SplashSource {
  readonly src: string;
  readonly type: string;
}

/** The film, best first. Order is the offer order: a browser takes the first source it can decode. */
export const SPLASH_SOURCES: readonly SplashSource[] = [
  { src: splashFilmWebm, type: 'video/webm' },
  { src: splashFilmMp4, type: 'video/mp4' },
];

/** The artwork as one frame, for the paths where no film plays. */
export const SPLASH_STILL: string = splashStillWebp;

export interface SplashPlan {
  /** Milliseconds of scrim before the artwork appears. Zero on the shipped artwork. */
  readonly holdMs: number;
  /** Milliseconds the artwork is on screen, dissolve included — not added to. */
  readonly showMs: number;
  /** Milliseconds of dissolve back to the editor. */
  readonly dissolveMs: number;
  /** When the dissolve begins, measured from mount. Inside the show, overlapping its tail. */
  readonly dissolveAtMs: number;
  /** Total time from mount to the layer being gone, if nobody skips. */
  readonly totalMs: number;
  /** True when the still is shown in place of the film. */
  readonly still: boolean;
}

export interface SplashPlanOptions {
  /** The reader has asked for reduced motion, so the film is replaced by the still. */
  reducedMotion?: boolean;
  holdMs?: number;
  dissolveMs?: number;
}

/**
 * How long each part of the opening lasts.
 *
 * Split out from the DOM so the two decisions worth checking can be checked without a browser: that
 * asking for reduced motion removes the film rather than merely shortening it, and that the
 * dissolve is folded into the show rather than appended to it.
 */
export function splashPlan(options: SplashPlanOptions = {}): SplashPlan {
  const still = options.reducedMotion === true;
  const holdMs = options.holdMs ?? SPLASH_HOLD_MS;
  const showMs = still ? SPLASH_STILL_MS : SPLASH_FILM_MS;
  // Clamped: a dissolve longer than the thing it is dissolving would start before the artwork does.
  const dissolveMs = Math.min(options.dissolveMs ?? SPLASH_DISSOLVE_MS, showMs);
  return {
    holdMs,
    showMs,
    dissolveMs,
    dissolveAtMs: holdMs + showMs - dissolveMs,
    totalMs: holdMs + showMs,
    still,
  };
}

/** Reads the reader's motion preference, defaulting to "no preference" where it cannot be asked. */
export function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

export interface SplashScreenOptions {
  /** Where the layer mounts. Defaults to `document.body` — it is its own layer, not part of #app. */
  container?: HTMLElement;
  /** Overrides the media query. Injected by callers that have already resolved the preference. */
  reducedMotion?: boolean;
  holdMs?: number;
  dissolveMs?: number;
}

export class SplashScreen {
  /** Resolves when the layer has left the DOM, whether it played out or was skipped. */
  readonly finished: Promise<void>;

  readonly plan: SplashPlan;

  private readonly root: HTMLElement;
  private readonly film: HTMLVideoElement | null;
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private settle: () => void = () => {};
  private phase: SplashPhase = 'held';

  /** Mounts the splash and starts the clock. */
  static show(options: SplashScreenOptions = {}): SplashScreen {
    return new SplashScreen(options);
  }

  constructor(options: SplashScreenOptions = {}) {
    const reducedMotion = options.reducedMotion ?? prefersReducedMotion();
    this.plan = splashPlan({
      reducedMotion,
      ...(options.holdMs === undefined ? {} : { holdMs: options.holdMs }),
      ...(options.dissolveMs === undefined ? {} : { dissolveMs: options.dissolveMs }),
    });
    this.finished = new Promise<void>((resolve) => {
      this.settle = resolve;
    });

    this.film = this.plan.still ? null : buildFilm();
    const still = el('img', {
      class: 'lg-splash-art lg-splash-still',
      attrs: { src: SPLASH_STILL, alt: '', decoding: 'async' },
    });

    this.root = el(
      'div',
      {
        class: 'lg-splash',
        // Decorative and self-dismissing: there is nothing here to announce or to tab to.
        attrs: { 'aria-hidden': 'true', role: 'presentation' },
        dataset: { phase: 'held', mode: this.film ? 'film' : 'still' },
        style: { ['--lg-splash-dissolve' as string]: `${this.plan.dissolveMs}ms` },
        on: { pointerdown: this.onSkip },
      },
      this.film,
      still,
    );

    (options.container ?? document.body).appendChild(this.root);
    globalThis.addEventListener?.('keydown', this.onSkip);

    this.after(this.plan.holdMs, () => void this.reveal());
  }

  /** Cut to the dissolve. Safe at any point, including before the mark has appeared. */
  skip(): void {
    this.dissolve();
  }

  /** Tear the layer down now, with no dissolve. Resolves {@link finished} so nothing is left waiting. */
  dispose(): void {
    if (this.phase === 'done') return;
    this.phase = 'done';
    for (const timer of this.timers) clearTimeout(timer);
    this.timers.clear();
    globalThis.removeEventListener?.('keydown', this.onSkip);
    this.film?.removeEventListener('ended', this.onEnded);
    // Pausing first: a detached <video> that is still playing keeps its decoder alive in some
    // browsers, and this one is 1080p.
    this.film?.pause();
    this.root.remove();
    this.settle();
  }

  private readonly onSkip = (): void => {
    this.dissolve();
  };

  private readonly onEnded = (): void => {
    this.dissolve();
  };

  private async reveal(): Promise<void> {
    if (!this.isPhase('held')) return;
    this.setPhase('showing');

    const film = this.film;
    if (!film) {
      this.armDissolve(this.plan.showMs);
      return;
    }

    try {
      await film.play();
    } catch {
      // Autoplay refused, or nothing here is decodable. Either way the still is the same artwork
      // and needs no permission, so the opening degrades to a quieter version of itself.
      this.fallBackToStill();
      return;
    }
    // `play()` can resolve after a skip or a dispose; the phase check keeps that from restarting a
    // layer that is already on its way out.
    if (!this.isPhase('showing')) {
      film.pause();
      return;
    }

    // The clock, not the film, decides when the dissolve starts — it has to begin *during* the
    // film's own fade-out, which no event announces. `ended` is kept only as an early out for a
    // film that runs shorter than its encode claims; by the time it normally fires the dissolve is
    // already under way and `dissolve` ignores it.
    film.addEventListener('ended', this.onEnded, { once: true });
    this.armDissolve(this.plan.showMs);
  }

  private fallBackToStill(): void {
    if (this.phase !== 'showing') return;
    this.root.dataset['mode'] = 'still';
    this.armDissolve(SPLASH_STILL_MS);
  }

  /** Schedules the dissolve to *end* `showMs` after the artwork appeared, not to start there. */
  private armDissolve(showMs: number): void {
    this.after(Math.max(0, showMs - this.plan.dissolveMs), () => this.dissolve());
  }

  private dissolve(): void {
    if (this.phase === 'dissolving' || this.phase === 'done') return;
    this.setPhase('dissolving');
    this.after(this.plan.dissolveMs, () => this.dispose());
  }

  private setPhase(phase: SplashPhase): void {
    this.phase = phase;
    this.root.dataset['phase'] = phase;
  }

  /**
   * Asked through a call rather than by reading the field, because `reveal` tests the phase both
   * before and after it changes it — and a direct comparison narrows the field to the first
   * answer, which makes the second test look to the compiler like a contradiction.
   */
  private isPhase(phase: SplashPhase): boolean {
    return this.phase === phase;
  }

  private after(delayMs: number, run: () => void): void {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      run();
    }, delayMs);
    this.timers.add(timer);
  }
}

function buildFilm(): HTMLVideoElement {
  const film = el('video', {
    class: 'lg-splash-art lg-splash-film',
    attrs: {
      muted: true,
      playsinline: true,
      preload: 'auto',
      tabindex: -1,
    },
  });
  // Deliberately no `poster`. A poster paints the moment the element is in the DOM, which would put
  // the mark on screen at mount and quietly delete the held second this screen exists to provide.
  // Nothing is lost by leaving it off: the film's own first frame is empty, so it fades up from
  // nothing either way.
  // Set as a property as well as an attribute: several browsers only consult the property when
  // deciding whether an unprompted `play()` is allowed, and this one has no audio track to mute.
  film.muted = true;
  film.defaultMuted = true;
  for (const source of SPLASH_SOURCES) {
    film.appendChild(el('source', { attrs: { src: source.src, type: source.type } }));
  }
  return film;
}
